import { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuth } from './context/AuthContext'
import { useIsMobile } from './hooks/useIsMobile'
import MobileGate from './components/MobileGate'
import DevBanner from './components/DevBanner'
import WriteErrorToast from './components/WriteErrorToast'
import { IS_DEV, HIDE_ENV_BADGE } from './lib/firebase'
import { inFoundry, inTeos } from './lib/shellPaths'

// EACH SURFACE DOWNLOADS ONLY ITS OWN CODE. The public pages (a DSP's availability link, the
// community and satisfaction surveys, the kiosk, the grid) are opened on phones and tablets by people
// with no InCite account. Until 2026-10-19 every page was imported here directly, so opening an
// emailed availability link downloaded the whole portal, about 6 MB of code (1.25 MB over the wire),
// before the form appeared. Now each page below is its own chunk, fetched when it is shown, and the
// signed-in portal (every staff page and shell) is one more chunk, src/Portal.jsx.
//
// Keep it that way: a page anyone can open without an account is a lazy import here, never a static
// one, and a staff page goes in Portal.jsx. Guarded by tests/unit/public-pages-lazy.test.js.
const Kiosk = lazy(() => import('./pages/Kiosk'))
const ServiceMatrix = lazy(() => import('./pages/ServiceMatrix'))
const CommunityVoice = lazy(() => import('./pages/CommunityVoice'))
const CsatPublic = lazy(() => import('./pages/CsatPublic'))
const AvailabilityForm = lazy(() => import('./pages/AvailabilityForm'))
const MyCode = lazy(() => import('./pages/MyCode'))
const MyTraining = lazy(() => import('./pages/MyTraining'))
const LearningStart = lazy(() => import('./pages/LearningStart'))
const AuthAction = lazy(() => import('./pages/AuthAction'))
const Login = lazy(() => import('./pages/Login'))
const MfaEnroll = lazy(() => import('./pages/MfaEnroll'))
const loadPortal = () => import('./Portal')
const Portal = lazy(loadPortal)
const loadIdent = () => import('./components/InciteIdent')
const InciteIdent = lazy(loadIdent)
// Warm the portal and the ident that opens it. A failed fetch here is ignored: the lazy load that
// renders them fetches again and reports through the error boundary.
function preloadPortal() { loadPortal().catch(() => {}); loadIdent().catch(() => {}) }

function Loading() {
  return <div className="h-screen flex items-center justify-center text-ink-soft">Loading…</div>
}

// Is this window the Service Grid? True for the route itself, for anything under it, for an
// explicit ?app=grid launch flag, and for a standalone (home-screen) window whose device has
// already run the grid — which is how a shortcut saved with the old start_url recovers.
function isServiceGrid(pathname) {
  if (pathname === '/matrix' || pathname.startsWith('/matrix/')) return true
  // /teri is the URL ABLE was given and the one saved in their shortcuts. Hosting serves the grid
  // HTML there; the router has to agree, or the bundle renders the portal login inside it.
  if (pathname === '/teri' || pathname.startsWith('/teri/')) return true
  try {
    const params = new URLSearchParams(window.location.search)
    if (params.get('app') === 'grid') return true
    const standalone = window.navigator.standalone === true
      || window.matchMedia?.('(display-mode: standalone)')?.matches
    if (standalone && localStorage.getItem('sg.isGridApp') === '1') return true
  } catch { /* storage or matchMedia unavailable — fall through to the portal */ }
  return false
}

// MFA enforcement gate (HIPAA: person/entity authentication). ENFORCED ON PROD ONLY.
// TOTP is enabled on the PROD Identity Platform project (2026-06-24), so every signed-in prod
// user must enroll an authenticator (see MfaEnroll.jsx) before reaching the app. The staging
// sandbox holds demo data (no PHI) and its Identity Platform has NO TOTP provider, so enforcing
// there only locks users out at the enroll step ("MFA isn't enabled on this project") — hence
// prod-only via IS_DEV. (Set up TOTP on the staging project later if full parity is wanted.)
const MFA_ENFORCED = !IS_DEV

// ---------------------------------------------------------------------------
// The InCite ident plays on the two transitions that mean "entering InCite":
// signing in, and leaving the Grant Foundry for the tenant app. It is a LOADING
// SCREEN — it covers auth resolving and the org's Firestore listeners attaching,
// so that work happens behind the brand moment instead of behind a spinner.
//
// It deliberately does NOT play on ordinary navigation inside InCite, and never
// over the Foundry, which has its own ident (GrantFoundry.jsx). Playing both would
// stack two full-screen animations on one transition.
// teOS has its own ident too (TeosIdent.jsx). Same rule as the Foundry: arriving there is that
// module's moment, and firing the InCite ident as well would stack two full-screen animations
// on one transition — the second one starting while the first is still fading.
// (inFoundry and inTeos live in src/lib/shellPaths.js, shared with the portal.)
const ownsIdent = (p) => inFoundry(p) || inTeos(p)

function useInciteIdent(user, pathname, enabled) {
  const [playing, setPlaying] = useState(false)
  const prevPath = useRef(null)
  const bootedFor = useRef(null)

  // 1. Sign-in / app boot with a live session. Keyed on uid rather than a boolean so
  //    switching accounts replays it, and signing out re-arms it.
  useEffect(() => {
    if (!user) { bootedFor.current = null; return }
    if (bootedFor.current === user.uid) return
    bootedFor.current = user.uid
    // Landing straight on a Foundry deep link is the Foundry's moment, not ours.
    if (enabled && !ownsIdent(pathname)) setPlaying(true)
  }, [user, enabled]) // eslint-disable-line react-hooks/exhaustive-deps

  // 2. Foundry -> InCite. Compares against the PREVIOUS pathname, so it fires on the
  //    transition itself rather than on every render while inside InCite.
  useEffect(() => {
    const prev = prevPath.current
    prevPath.current = pathname
    if (enabled && prev && inFoundry(prev) && !ownsIdent(pathname)) setPlaying(true)
  }, [pathname, enabled])

  return [playing, useCallback(() => setPlaying(false), [])]
}

export default function App() {
  const { user, loading, mfaEnrolled, ssoUser, claims, signOut } = useAuth()
  const { pathname } = useLocation()
  const isMobile = useIsMobile()

  // Gated on the same conditions that let the app render at all: never over the phone
  // notice, the password-action page, the login form, or the MFA enrol step — covering
  // a form the user has to interact with would be a trap, not a loading screen.
  const identAllowed = !isMobile && pathname !== '/auth/action' && !loading
    && !!user && !(MFA_ENFORCED && !mfaEnrolled && !ssoUser)
  const [identPlaying, endIdent] = useInciteIdent(user, pathname, identAllowed)

  // InCite is desktop-only for now — phones see a simple notice instead of the app.
  let content
  // THE KIOSK IS A TABLET SURFACE, NOT A STAFF PAGE — and it renders before BOTH gates.
  //
  // Auth: a device bolted to a wall in a thrift store has no staff session; the person using it
  // identifies with a four-digit PIN. It signs in anonymously and every read goes through the
  // server, which resolves the PIN scoped to the tablet's registered org and returns a first name
  // and nothing else — no roster, no Medicaid IDs.
  //
  // THE KIOSK IS ALSO EXEMPT FROM THE MOBILE GATE. It is designed for a tablet — that is the entire
  // point of the surface — so blocking small screens here blocked the only device it runs on.
  // Checked before isMobile, which otherwise wins for exactly the form factor this page targets.
  if (pathname === '/kiosk') content = <Kiosk />
  // THE SERVICE GRID IS A SEPARATE APP, NOT A PAGE OF THE PORTAL.
  //
  // Sean, 2026-08-27: "I do not want this app to just be a front end for the portal, the goal is
  // for it to be separate." A DSP has no InCite account, sees no dashboard and no org switcher —
  // the device signs in anonymously and the SHIFT sign-in is the whole identity. So it renders here
  // like the kiosk, ahead of the mobile gate and ahead of the portal's auth chain, rather than as a
  // route inside the tenant shell.
  //
  // An earlier version put it inside <Routes>, which is why it needed the org switcher and a portal
  // login just to reach the grid.
  // MATCHED ON PREFIX, AND ON A LAUNCH FLAG, not on an exact path.
  //
  // iOS bakes start_url into a home-screen shortcut AT CREATION and never refreshes it. Every
  // shortcut ABLE made before the manifest was fixed still opens "/" — which falls through this
  // chain to the InCite email login, exactly what Melissa Wolf reported twice (2026-09-03/04).
  // Correcting the manifest fixes NEW installs and does nothing for the ones already on the iPads.
  //
  // So the grid also answers to a marker that survives in the saved URL and in storage:
  //   ?app=grid  — what /teri now redirects to, so a shortcut made from it carries the flag
  //   sg.isGridApp — set on first grid load; a standalone window that lands on "/" self-corrects
  // A stale shortcut therefore lands on the grid instead of a login it can never satisfy.
  else if (isServiceGrid(pathname)) content = <ServiceMatrix />
  // COMMUNITY VOICE — the public questionnaire behind ROMA's Community Input page (/voice/{org}).
  // No portal login, and AHEAD of the mobile gate: a respondent at a pantry is on a phone, and the
  // gate would hand them the "use a desktop" screen instead of the survey.
  else if (pathname.startsWith('/voice/')) content = <CommunityVoice slug={decodeURIComponent(pathname.slice('/voice/'.length).split('/')[0])} />
  // CLIENT SATISFACTION — one program's public survey link (/satisfaction/{org}/{surveyId}), turned
  // on from /roma/satisfaction. Same door as the questionnaire above: no login, ahead of the gates.
  else if (pathname.startsWith('/satisfaction/')) content = <CsatPublic org={decodeURIComponent(pathname.slice('/satisfaction/'.length).split('/')[0] || '')} surveyId={decodeURIComponent(pathname.slice('/satisfaction/'.length).split('/')[1] || '')} />
  // DSP AVAILABILITY — a direct-support worker's personal link (/availability/{token}). No InCite
  // account, so it renders here like the survey above, ahead of the portal's auth chain.
  else if (pathname.startsWith('/availability/')) content = <AvailabilityForm token={pathname.slice('/availability/'.length).split('/')[0]} />
  // CHANGE YOUR IPAD CODE — a staff member's page for choosing a new four-digit code (/my-code), from any
  // browser, with no InCite account (owner, 2026-10-19). Same door as the pages above: ahead of the gates.
  else if (pathname === '/my-code') content = <MyCode />
  // YOUR TRAINING RECORD — where a trainee records a track finished in the training portal, and a supervisor
  // beside them signs it off, each with their employee ID and code (/my-training, proof of skill, 2026-10-20).
  // No InCite account, so the same door as /my-code.
  else if (pathname === '/my-training') content = <MyTraining />
  // SIGN IN TO INCITE LEARNING — the training portal's sign-in (/learning/start, owner 2026-10-20): the employee ID and
  // code, answered with the person's own path from their Harbor role. Hosting sends this one address to the app
  // (firebase.json, ahead of /learning/**, which is the portal's own page). Same door as /my-code.
  else if (pathname === '/learning/start') content = <LearningStart />
  // Past the public pages, a desktop visit is headed for the portal: the sign-in form, or the app
  // itself. Start fetching its code now, while sign-in resolves, rather than after. Public pages
  // and phones never fetch it.
  const publicPage = content !== undefined
  const wantsPortal = !publicPage && !isMobile
  useEffect(() => { if (wantsPortal) preloadPortal() }, [wantsPortal])
  if (publicPage) { /* chosen above */ }
  else if (isMobile) content = <MobileGate />
  // Public email-action handler (set password from invite). Reachable signed-in or out.
  else if (pathname === '/auth/action') content = <AuthAction />
  else if (loading) content = <Loading />
  else if (!user) content = <Login />
  // HIPAA: every PROD user must enroll a second factor before reaching the app. The sandbox
  // skips this (MFA_ENFORCED=!IS_DEV) so UAT isn't blocked by an enroll step staging can't satisfy.
  // A federated (SSO) session was already MFA'd by the customer's own Entra Conditional Access —
  // demanding OUR TOTP on top would defeat the point of single sign-on. Password sessions keep the
  // TOTP requirement exactly as before.
  else if (MFA_ENFORCED && !mfaEnrolled && !ssoUser) content = <MfaEnroll />
  // Authenticated but NOT provisioned: a real identity (usually a federated sign-in from a
  // customer's directory) with no tenant claim. Deny-by-default already blocks every read; this
  // screen exists so that state reads as "ask your administrator", not as a broken app. Without it
  // the shell renders and every query silently fails.
  else if (!claims?.organizationDomain && !claims?.platformAdmin) content = (
    <div className="min-h-screen flex items-center justify-center bg-[#EFF3EA] p-4">
      <div className="w-full max-w-[420px] bg-white rounded-2xl shadow-[0_10px_30px_rgba(32,64,26,0.12)] p-7 text-center">
        <h1 className="text-xl font-bold text-[#20401A] mb-2">Almost there</h1>
        <p className="text-sm text-ink-soft leading-relaxed mb-5">
          You signed in as <span className="font-semibold">{user.email}</span>, but this account has not been
          added to an organization yet. Ask your administrator to invite you to InCite, then sign in again.
        </p>
        <button type="button" onClick={signOut}
          className="px-4 py-2 rounded-lg bg-[#67A629] text-white text-sm font-semibold hover:bg-[#5C9322]">
          Sign out
        </button>
      </div>
    </div>
  )
  // Everything past provisioning is the signed-in portal: Scout Console, the Grant Foundry, teOS,
  // ROMA and the tenant layout with every staff page (src/Portal.jsx, its own chunk).
  else content = <Portal />

  // DEV/sandbox builds get a loud, non-interactive marker so the sandbox is never mistaken
  // for prod. IS_DEV is false on prod (and on plain `npm run dev`), so the badge stays off there.
  //
  // HIDE_ENV_BADGE IS THE APP REVIEW ESCAPE, AND IT WAS NEVER READ HERE. src/lib/firebase.js has
  // exported it since build 7 and scripts/prepare-ipad-app.mjs sets it only on --review, but this
  // gate tested IS_DEV alone — so the flag suppressed nothing and the amber "DEV Sandbox · not
  // production" ring shipped to App Review anyway (found 2026-09-26, while removing NFC for build
  // 10). The build guard missed it too: it walks only the matrix-* chunk graph, and the banner is
  // in main-*.js.
  //
  // The flag cannot be set by an ordinary build — prepare-ipad-app refuses --review unless the
  // bundle is staging — so the decision to hide the marker still lives beside the decision to ship
  // staging at all.
  // The ident renders LAST and fixed, so it covers the app (and the dev banner) while the
  // real page mounts and its listeners attach underneath.
  return (
    <>
      {IS_DEV && !HIDE_ENV_BADGE && <DevBanner />}
      {/* Every surface is a lazy chunk; this shows while the one being opened downloads. */}
      <Suspense fallback={<Loading />}>{content}</Suspense>
      {/* App-wide surface for denied/failed Firestore writes — see src/lib/writeGuard.js. */}
      <WriteErrorToast />
      {identPlaying && identAllowed && <Suspense fallback={null}><InciteIdent onDone={endIdent} /></Suspense>}
    </>
  )
}
